import { useState } from 'react';
import type { App, Initiative } from '../lib/types';
import { PHASE_LABEL, STATUSES, STATUS_LABEL } from '../lib/types';
import InitiativeRow from './InitiativeRow';

interface Props {
  app: App;
  initiatives: Initiative[];
  onEditInitiative: (initiative: Initiative) => void;
  onAddInitiative: () => void;
  onEditApp: () => void;
}

export default function AppCard({ app, initiatives, onEditInitiative, onAddInitiative, onEditApp }: Props) {
  const [open, setOpen] = useState(true);

  const grouped = STATUSES.map((s) => ({
    status: s,
    items: initiatives.filter((i) => i.status === s),
  })).filter((g) => g.items.length > 0);

  const done = initiatives.filter((i) => i.status === 'done').length;
  const total = initiatives.length;

  return (
    <div className="rounded-xl border border-white/5 bg-ink-900/40">
      <button
        className="w-full flex items-center gap-3 p-3 text-left"
        onClick={() => setOpen((o) => !o)}
      >
        <span className={`chip chip-phase-${app.phase}`}>{PHASE_LABEL[app.phase]}</span>
        <span className="font-medium">{app.name}</span>
        {app.domain && (
          <a
            href={app.domain.startsWith('http') ? app.domain : `https://${app.domain}`}
            target="_blank"
            rel="noreferrer"
            className="text-[11px] text-ink-400 hover:text-ink-200 underline-offset-2 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {app.domain}
          </a>
        )}
        <span className="ml-auto text-[11px] text-ink-400">
          {done}/{total}
        </span>
        <span className="text-ink-400">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="px-3 pb-3">
          {app.description && (
            <p className="text-xs text-ink-300 mb-2">{app.description}</p>
          )}
          {app.stack_notes && (
            <p className="text-[11px] text-ink-500 italic mb-3">{app.stack_notes}</p>
          )}

          <div className="flex items-center gap-2 mb-2">
            <button className="btn text-xs" onClick={onAddInitiative}>+ Iniciativa</button>
            <button className="btn text-xs" onClick={onEditApp}>Editar app</button>
          </div>

          {grouped.length === 0 ? (
            <div className="text-[11px] text-ink-500 border border-dashed border-white/10 rounded-md p-3 text-center">
              Sin iniciativas.
            </div>
          ) : (
            <div className="space-y-3">
              {grouped.map((g) => (
                <div key={g.status}>
                  <div className="text-[10px] uppercase tracking-widest text-ink-500 mb-1.5">
                    {STATUS_LABEL[g.status]} · {g.items.length}
                  </div>
                  <div className="space-y-1.5">
                    {g.items.map((ini) => (
                      <InitiativeRow
                        key={ini.id}
                        initiative={ini}
                        onClick={() => onEditInitiative(ini)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
